import { getFootballSeasonYear } from '../../../utils/season';
// src/components/forms/TeamSelection.jsx - FIXED VERSION (SSR-safe + logger safety + dynamic season)
import React, { useMemo, useCallback, useRef } from "react";
import { transitionToTeamTheme, hasCustomColors } from '../../utils/teamColors.js';
import { logger } from "../../utils/logger";

const hasWindow = typeof window !== "undefined";
const hasDocument = typeof document !== "undefined";
const safeLocalStorage = hasWindow ? window.localStorage : null;
const safeSessionStorage = hasWindow ? window.sessionStorage : null;
const currentYear = getFootballSeasonYear();

export const TeamSelection = ({ gameData, setGameData, setCurrentStep, updateGroup }) => {
  const continueButtonRef = useRef(null);

  const nflData = {
    AFC: {
      "AFC East": ["Buffalo Bills", "Miami Dolphins", "New England Patriots", "New York Jets"],
      "AFC North": ["Baltimore Ravens", "Cincinnati Bengals", "Cleveland Browns", "Pittsburgh Steelers"],
      "AFC South": ["Houston Texans", "Indianapolis Colts", "Jacksonville Jaguars", "Tennessee Titans"],
      "AFC West": ["Denver Broncos", "Kansas City Chiefs", "Las Vegas Raiders", "Los Angeles Chargers"]
    },
    NFC: {
      "NFC East": ["Dallas Cowboys", "New York Giants", "Philadelphia Eagles", "Washington Commanders"],
      "NFC North": ["Chicago Bears", "Detroit Lions", "Green Bay Packers", "Minnesota Vikings"],
      "NFC South": ["Atlanta Falcons", "Carolina Panthers", "New Orleans Saints", "Tampa Bay Buccaneers"],
      "NFC West": ["Arizona Cardinals", "Los Angeles Rams", "San Francisco 49ers", "Seattle Seahawks"]
    }
  };

  const collegeData = {
    SEC: {
      "SEC": ["Florida", "Georgia", "Kentucky", "Missouri", "South Carolina", "Tennessee", "Vanderbilt", "Alabama", "Arkansas", "Auburn", "LSU", "Mississippi State", "Ole Miss", "Texas A&M", "Texas", "Oklahoma"]
    },
    "Big Ten": {
      "Big Ten": ["Indiana", "Maryland", "Michigan", "Michigan State", "Ohio State", "Penn State", "Rutgers", "Illinois", "Iowa", "Minnesota", "Nebraska", "Northwestern", "Purdue", "Wisconsin", "Washington", "Oregon", "USC", "UCLA"]
    },
    "Big 12": {
      "Big 12": ["Arizona", "Arizona State", "Baylor", "BYU", "Cincinnati", "Colorado", "Houston", "Iowa State", "Kansas", "Kansas State", "Oklahoma State", "TCU", "Texas Tech", "UCF", "Utah", "West Virginia"]
    },
    "ACC": {
      "ACC": ["Boston College", "Clemson", "Florida State", "Louisville", "NC State", "Syracuse", "Wake Forest", "Duke", "Georgia Tech", "Miami", "North Carolina", "Pittsburgh", "Virginia", "Virginia Tech", "SMU", "Stanford", "California"]
    },
    "Pac-12 / Independents": {
      "Pac-12": ["Oregon State", "Washington State"],
      "Independents": ["Notre Dame", "Army", "Navy"]
    }
  };

  // Memoized team ID mapping for better performance
  const teamIdMap = useMemo(() => ({
    // NFL Teams (ESPN team IDs)
    // AFC East
    "Buffalo Bills": "2",
    "Miami Dolphins": "15",
    "New England Patriots": "17",
    "New York Jets": "20",

    // AFC North
    "Baltimore Ravens": "33",
    "Cincinnati Bengals": "4",
    "Cleveland Browns": "5",
    "Pittsburgh Steelers": "23",

    // AFC South
    "Houston Texans": "34",
    "Indianapolis Colts": "11",
    "Jacksonville Jaguars": "30",
    "Tennessee Titans": "10",

    // AFC West
    "Denver Broncos": "7",
    "Kansas City Chiefs": "12",
    "Las Vegas Raiders": "13",
    "Los Angeles Chargers": "24",

    // NFC East
    "Dallas Cowboys": "6",
    "New York Giants": "19",
    "Philadelphia Eagles": "21",
    "Washington Commanders": "28",

    // NFC North
    "Chicago Bears": "3",
    "Detroit Lions": "8",
    "Green Bay Packers": "9",
    "Minnesota Vikings": "16",

    // NFC South
    "Atlanta Falcons": "1",
    "Carolina Panthers": "29",
    "New Orleans Saints": "18",
    "Tampa Bay Buccaneers": "27",

    // NFC West
    "Arizona Cardinals": "22",
    "Los Angeles Rams": "14",
    "San Francisco 49ers": "25",
    "Seattle Seahawks": "26",

    // COLLEGE TEAMS
    // Big Ten East
    "Indiana": "84",
    "Maryland": "120",
    "Michigan": "130",
    "Michigan State": "127",
    "Ohio State": "194",
    "Penn State": "213",
    "Rutgers": "164",

    // Big Ten West
    "Illinois": "356",
    "Iowa": "2294",
    "Minnesota": "135",
    "Nebraska": "158",
    "Northwestern": "77",
    "Purdue": "2509",
    "Wisconsin": "275",

    // SEC East
    "Florida": "57",
    "Georgia": "61",
    "Kentucky": "96",
    "Missouri": "142",
    "South Carolina": "2579",
    "Tennessee": "2633",
    "Vanderbilt": "238",

    // SEC West
    "Alabama": "333",
    "Arkansas": "8",
    "Auburn": "2",
    "LSU": "99",
    "Mississippi State": "344",
    "Ole Miss": "145",
    "Texas A&M": "245",
    "Texas": "251",

    // Big 12
    "Arizona": "12",
    "Arizona State": "9",
    "Baylor": "239",
    "BYU": "252",
    "Cincinnati": "2132",
    "Colorado": "38",
    "Houston": "248",
    "Iowa State": "66",
    "Kansas": "2305",
    "Kansas State": "2306",
    "Oklahoma State": "197",
    "TCU": "2628",
    "Texas Tech": "2641",
    "UCF": "2116",
    "Utah": "254",
    "West Virginia": "277",

    // ACC
    "Boston College": "103",
    "Clemson": "228",
    "Florida State": "52",
    "Louisville": "97",
    "NC State": "152",
    "Syracuse": "183",
    "Wake Forest": "154",
    "Duke": "150",
    "Georgia Tech": "59",
    "Miami": "2390",
    "North Carolina": "153",
    "Pittsburgh": "221",
    "Virginia": "258",
    "Virginia Tech": "259",

    // Pac-12
    "Oregon State": "204",
    "Washington State": "265",

    // Independent
    "Notre Dame": "87",
    "Army": "349",
    "Navy": "2426"
  }), []);

  // Function to update group with team selection
  const updateGroupWithTeam = useCallback(async (selectedTeam) => {
    if (gameData?.isGroupCreator && gameData?.groupId && updateGroup) {
      try {
        console.log("🏈 Group Creator updating group with team selection:", selectedTeam);
        console.log("🏈 Group ID:", gameData.groupId);

        const groupUpdateData = {
          team: selectedTeam.name,
          teamId: selectedTeam.id,
          league: selectedTeam.league,
          conference: selectedTeam.conference || '',
          division: selectedTeam.division || '',
          season: gameData.season || currentYear,
          teamSelected: true
        };

        console.log("🏈 Calling updateGroup with data:", groupUpdateData);
        await updateGroup(gameData.groupId, groupUpdateData);
        console.log("✅ Group updated with team selection!");

        setGameData(prev => ({
          ...prev,
          teamSelected: true
        }));
      } catch (error) {
        console.error("❌ Error updating group with team:", error);
      }
    } else {
      console.log("❌ Cannot update group:", {
        isGroupCreator: gameData?.isGroupCreator,
        groupId: gameData?.groupId,
        updateGroupAvailable: !!updateGroup
      });
    }
  }, [gameData?.isGroupCreator, gameData?.groupId, gameData?.season, updateGroup, setGameData]);

  const handleLeagueChange = useCallback((league) => {
    setGameData(prev => ({
      ...prev,
      league,
      conference: "",
      division: "",
      team: "",
      teamId: ""
    }));
  }, [setGameData]);

  const handleConferenceChange = useCallback((conference) => {
    setGameData(prev => ({
      ...prev,
      conference,
      // For college, division is the same as conference, except for "Pac-12 / Independents"
      division: gameData.league === "College" && conference !== "Pac-12 / Independents" ? conference : "",
      team: "",
      teamId: ""
    }));
  }, [setGameData, gameData.league]);

  const handleDivisionChange = useCallback((division) => {
    setGameData(prev => ({
      ...prev,
      division,
      team: "",
      teamId: ""
    }));
  }, [setGameData]);

  // Enhanced team change handler with group update
  const handleTeamChange = useCallback(async (team) => {
    const teamId = teamIdMap[team] || "1";

    logger?.sports?.('🏈 === TEAM SELECTION DEBUG ===');
    logger?.sports?.('🏈 Selected team:', team);
    logger?.sports?.('🏈 Team ID:', teamId);
    logger?.sports?.('🏈 Is group creator:', gameData?.isGroupCreator);
    logger?.sports?.('🏈 Group ID:', gameData?.groupId);
    logger?.sports?.('🏈 Current gameData:', {
      league: gameData?.league,
      conference: gameData?.conference,
      division: gameData?.division,
      groupMode: gameData?.groupMode
    });
    logger?.sports?.('🏈 updateGroup function available:', typeof updateGroup === 'function');

    const selectedTeam = {
      name: team,
      id: teamId,
      league: gameData.league,
      conference: gameData.conference,
      division: gameData.division
    };

    logger?.sports?.('🏈 Team object to save:', selectedTeam);

    setGameData(prev => ({
      ...prev,
      team,
      teamId,
      teamSelected: false
    }));

    // Disable immersive theming changes here to keep First Snap neutral

    if (gameData?.isGroupCreator && gameData?.groupId) {
      logger?.sports?.('🏈 Attempting to update group...');
      await updateGroupWithTeam(selectedTeam);
    } else {
      logger?.sports?.('🏈 Skipping group update - not group creator or no group ID');
    }

    // AUTO-SCROLL TO CONTINUE BUTTON (client only)
    if (hasWindow) {
      setTimeout(() => {
        const el = continueButtonRef.current;
        if (el && typeof el.scrollIntoView === 'function') {
          const prefersReduced =
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

          el.scrollIntoView({
            behavior: prefersReduced ? 'auto' : 'smooth',
            block: 'center',
            inline: 'nearest'
          });

          logger?.completion?.('🎯 Auto-scrolled to continue button');
        } else {
          console.warn('⚠️ Continue button ref not found');
        }
      }, 400);
    }
  }, [setGameData, teamIdMap, gameData, updateGroupWithTeam, updateGroup]);

  const getCurrentData = useCallback(() => {
    return gameData.league === "NFL" ? nflData : collegeData;
  }, [gameData.league]);

  const getConferences = useCallback(() => {
    if (!gameData.league) return [];
    return Object.keys(getCurrentData());
  }, [gameData.league, getCurrentData]);

  const getDivisions = useCallback(() => {
    if (!gameData.conference) return [];
    const currentData = getCurrentData();
    return Object.keys(currentData[gameData.conference] || {});
  }, [gameData.conference, getCurrentData]);

  const getTeams = useCallback(() => {
    if (!gameData.division) return [];
    const currentData = getCurrentData();
    return currentData[gameData.conference]?.[gameData.division] || [];
  }, [gameData.division, gameData.conference, getCurrentData]);

  const canContinue = useCallback(() => {
    return Boolean(gameData.league && gameData.conference && gameData.division && gameData.team);
  }, [gameData.league, gameData.conference, gameData.division, gameData.team]);

  // 🔥 FIXED: Mark team as selected for solo games + store navigation context
  const handleTeamContinue = useCallback(() => {
    if (canContinue()) {
      console.log('Team selection complete:', {
        league: gameData.league,
        conference: gameData.conference,
        division: gameData.division,
        team: gameData.team,
        teamId: gameData.teamId,
        isGroupCreator: gameData.isGroupCreator,
        groupId: gameData.groupId
      });
      console.log('gameData.division:', gameData.division);

      setGameData(prev => ({
        ...prev,
        teamSelected: true
      }));

      const navigationContext = {
        fromTeamSelection: true,
        hasTeamData: true,
        timestamp: Date.now(),
        gameMode: gameData.groupMode || 'solo',
        isGroupCreator: gameData.isGroupCreator,
        teamSelection: {
          league: gameData.league,
          conference: gameData.conference,
          division: gameData.division,
          team: gameData.team,
          teamId: gameData.teamId
        }
      };

      if (safeLocalStorage) {
        safeLocalStorage.setItem('sbe-navigation-context', JSON.stringify(navigationContext));
      }
      logger?.navigation?.('📍 Stored team selection navigation context');

      setCurrentStep("profile");

      if (hasWindow) {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
    }
  }, [canContinue, gameData, setCurrentStep, setGameData]);

  const handleBackClick = useCallback(() => {
    logger?.component?.('🔄 Going back from team selection to dashboard...');

    // Clean up any payment abandonment data (client only)
    if (safeLocalStorage) {
      safeLocalStorage.removeItem('sbe-temp-game-data');
      safeLocalStorage.removeItem('sbe-temp-fan-type');
      safeLocalStorage.removeItem('sbe-temp-current-step');
      safeLocalStorage.removeItem('sbe-payment-timestamp');
      safeLocalStorage.removeItem('sbe-navigation-context');
    }
    if (safeSessionStorage) {
      safeSessionStorage.removeItem('stripe-payment-active');
      safeSessionStorage.removeItem('stripe-payment-start');
    }

    setGameData(prev => ({
      ...prev,
      groupMode: null,
      league: "",
      conference: "",
      division: "",
      team: "",
      teamId: "",
      teamSelected: false,
      fanType: null,
      formActivated: false,
      _paymentFlowActive: false,
      _redirectingToStripe: false,
      _paymentCancelled: false,
      _allowPaymentRetry: false,
      _lastPaymentAttempt: null
    }));

    setCurrentStep("dashboard");

    if (hasWindow) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [setCurrentStep, setGameData]);

  if (!gameData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        fontSize: '16px',
        color: '#6b7280'
      }}>
        Loading team selection...
      </div>
    );
  }

  return (
    <div className="sbe-container" style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 1rem'
    }}>

      {/* PAGE CONTENT WITH PROPER SPACING */}
      <div className="sbe-main" style={{
        flex: 1,
        padding: '2rem 0'
      }}>

        {/* SELECT YOUR TEAM CARD */}
        <div className="sbe-team-selection-container" style={{
          maxWidth: '480px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
          padding: '32px 24px'
        }}>

          {/* HEADER */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '24px',
            textAlign: 'left'
          }}>
            <div style={{ fontSize: '32px', flexShrink: 0 }}>🏈</div>
            <div style={{ flex: 1 }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#257980',
                margin: '0 0 4px 0'
              }}>
                Select Your Team
              </h2>
              <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>
                Choose your favorite team to analyze for the {gameData.season || currentYear} season
              </p>
            </div>
          </div>

          {/* Group context indicator */}
          {gameData.groupMode !== 'solo' && (
            <div style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #257980',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              textAlign: 'center',
              fontSize: '14px'
            }}>
              {gameData.isGroupCreator ? (
                <span style={{ color: '#257980' }}>
                  👑 <strong>Group Creator:</strong> Your team selection will set the team for the entire group
                </span>
              ) : (
                <span style={{ color: '#10b981' }}>
                  👥 <strong>Group Member:</strong> Select the same team as your group
                </span>
              )}
            </div>
          )}

          {/* Group update status */}
          {gameData.isGroupCreator && gameData.team && gameData.teamSelected && (
            <div style={{
              backgroundColor: 'var(--sbe-primary-bg)',
              border: '1px solid #16a34a',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '12px',
              textAlign: 'center',
              fontSize: '14px'
            }}>
              <span style={{ color: 'var(--sbe-primary-dark)' }}>
                ✅ <strong>Group Updated:</strong> {gameData.team} has been set for all group members
              </span>
            </div>
          )}

          {/* FORM */}
          <div className="sbe-form-container" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>

            {/* League */}
            <div className="sbe-form-section">
              <label style={{
                display: 'block',
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                League <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  backgroundColor: 'white',
                  color: '#1f2937',
                  transition: 'border-color 0.2s',
                  outline: 'none',
                  cursor: 'pointer'
                }}
                value={gameData.league || ""}
                onChange={(e) => handleLeagueChange(e.target.value)}
                onFocus={(e) => e.target.style.borderColor = '#257980'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              >
                <option value="">Select a league...</option>
                <option value="NFL">NFL</option>
                <option value="College">College Football</option>
              </select>
            </div>

            {/* Conference */}
            <div className="sbe-form-section">
              <label style={{
                display: 'block',
                fontSize: '16px',
                fontWeight: '600',
                color: !gameData.league ? '#9ca3af' : '#374151',
                marginBottom: '8px'
              }}>
                Conference <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `2px solid ${!gameData.league ? '#e5e7eb' : '#d1d5db'}`,
                  borderRadius: '8px',
                  fontSize: '16px',
                  backgroundColor: !gameData.league ? '#f9fafb' : 'white',
                  color: !gameData.league ? '#9ca3af' : '#1f2937',
                  cursor: !gameData.league ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  outline: 'none'
                }}
                value={gameData.conference || ""}
                onChange={(e) => handleConferenceChange(e.target.value)}
                disabled={!gameData.league}
                onFocus={(e) => !e.target.disabled && (e.target.style.borderColor = '#257980')}
                onBlur={(e) => !e.target.disabled && (e.target.style.borderColor = '#d1d5db')}
              >
                <option value="">Select a conference...</option>
                {getConferences().map(conference => (
                  <option key={conference} value={conference}>{conference}</option>
                ))}
              </select>
            </div>

            {/* Division */}
            <div className="sbe-form-section">
              <label style={{
                display: 'block',
                fontSize: '16px',
                fontWeight: '600',
                color: !gameData.conference ? '#9ca3af' : '#374151',
                marginBottom: '8px'
              }}>
                Division <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `2px solid ${!gameData.conference ? '#e5e7eb' : '#d1d5db'}`,
                  borderRadius: '8px',
                  fontSize: '16px',
                  backgroundColor: !gameData.conference ? '#f9fafb' : 'white',
                  color: !gameData.conference ? '#9ca3af' : '#1f2937',
                  cursor: !gameData.conference ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  outline: 'none'
                }}
                value={gameData.division || ""}
                onChange={(e) => handleDivisionChange(e.target.value)}
                disabled={!gameData.conference}
                onFocus={(e) => !e.target.disabled && (e.target.style.borderColor = '#257980')}
                onBlur={(e) => !e.target.disabled && (e.target.style.borderColor = '#d1d5db')}
              >
                <option value="">Select a division...</option>
                {getDivisions().map(division => (
                  <option key={division} value={division}>{division}</option>
                ))}
              </select>
            </div>

            {/* Team */}
            <div className="sbe-form-section">
              <label style={{
                display: 'block',
                fontSize: '16px',
                fontWeight: '600',
                color: !gameData.division ? '#9ca3af' : '#374151',
                marginBottom: '8px'
              }}>
                Team <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `2px solid ${!gameData.division ? '#e5e7eb' : '#d1d5db'}`,
                  borderRadius: '8px',
                  fontSize: '16px',
                  backgroundColor: !gameData.division ? '#f9fafb' : 'white',
                  color: !gameData.division ? '#9ca3af' : '#1f2937',
                  cursor: !gameData.division ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  outline: 'none'
                }}
                value={gameData.team || ""}
                onChange={(e) => handleTeamChange(e.target.value)}
                disabled={!gameData.division}
                onFocus={(e) => !e.target.disabled && (e.target.style.borderColor = '#257980')}
                onBlur={(e) => !e.target.disabled && (e.target.style.borderColor = '#d1d5db')}
              >
                <option value="">Select your team...</option>
                {getTeams().map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>

            {/* Selection summary */}
            {gameData.team && (
              <div style={{
                backgroundColor: '#f0f9ff',
                padding: '20px',
                borderRadius: '12px',
                border: '2px solid #257980',
                marginBottom: '24px',
                animation: 'fadeIn 0.3s ease-in'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#257980',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  ✅ Your Selection
                </div>
                <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1f2937',
                    marginBottom: '8px'
                  }}>
                    {gameData.team}
                  </div>
                  <div style={{ color: '#6b7280' }}>
                    {gameData.division} • {gameData.conference} • {gameData.league}
                  </div>
                  <div style={{
                    color: '#257980',
                    fontSize: '14px',
                    fontWeight: '600',
                    marginTop: '4px'
                  }}>
                    Team ID: {gameData.teamId} • {gameData.season || currentYear} Season
                  </div>
                  {gameData.isGroupCreator && (
                    <div style={{
                      color: gameData.teamSelected ? 'var(--sbe-primary-dark)' : '#f59e0b',
                      fontSize: '12px',
                      fontWeight: '600',
                      marginTop: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      {gameData.teamSelected ? '✅ Group Updated' : '⏳ Updating Group...'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Nav buttons */}
            <div className="sbe-navigation-buttons" style={{ display: 'flex', gap: '16px', marginTop: '32px' }}>
              <button
                type="button"
                className="sbe-button-secondary"
                style={{
                  flex: 1, padding: '12px 24px', backgroundColor: 'white',
                  border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px',
                  fontWeight: '600', color: '#374151', cursor: 'pointer', transition: 'all 0.2s'
                }}
                onClick={handleBackClick}
                onMouseEnter={(e) => { e.target.style.backgroundColor = '#f9fafb'; e.target.style.borderColor = '#9ca3af'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'white'; e.target.style.borderColor = '#e5e7eb'; }}
              >
                ← Back
              </button>

              <button
                type="button"
                ref={continueButtonRef}
                className="sbe-button-primary"
                style={{
                  flex: 2, padding: '12px 24px', fontSize: '16px',
                  backgroundColor: canContinue() ? '#257980' : '#9ca3af',
                  color: canContinue() ? 'white' : '#6b7280',
                  border: 'none', borderRadius: '8px',
                  cursor: canContinue() ? 'pointer' : 'not-allowed',
                  fontWeight: '600', transition: 'all 0.2s', outline: 'none',
                  scrollMarginTop: '2rem'
                }}
                onClick={handleTeamContinue}
                disabled={!canContinue()}
                onMouseEnter={(e) => { if (canContinue()) e.target.style.backgroundColor = '#1e6165'; }}
                onMouseLeave={(e) => { if (canContinue()) e.target.style.backgroundColor = '#257980'; }}
              >
                {canContinue() ? 'Continue →' : 'Select Team to Continue'}
              </button>
            </div>
          </div>
        </div>

        {/* PROGRESS DOTS */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginTop: '24px'
        }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#257980' }} />
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#257980' }} />
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#e5e7eb' }} />
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#e5e7eb' }} />
        </div>

        {/* Debug info for development */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{
            marginTop: '24px',
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            fontSize: '12px',
            color: '#6b7280',
            textAlign: 'center'
          }}>
            <strong>Debug Info:</strong><br />
            Group Creator: {gameData.isGroupCreator ? 'Yes' : 'No'}<br />
            Group ID: {gameData.groupId || 'None'}<br />
            Team Selected: {gameData.teamSelected ? 'Yes' : 'No'}<br />
            Update Group Function: {typeof updateGroup === 'function' ? 'Available' : 'Missing'}
          </div>
        )}

      </div>
    </div>
  );
};

export default TeamSelection;
